import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import {
  Organisation,
  PermissionLevel,
  TaskPlan,
  Team,
  User as TPUser,
} from "../types";
import {
  deleteShare,
  getTeams,
  getUsers,
  saveShare,
} from "../services/storageService";
import { Globe, Search, Trash2, User, Users, XCircle } from "./Icons";

interface ShareModalProps {
  plan: TaskPlan;
  currentOrg: Organisation;
  onClose: () => void;
  onUpdate: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({
  plan,
  currentOrg,
  onClose,
  onUpdate,
}) => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState<TPUser[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [selectedPermission, setSelectedPermission] =
    useState<PermissionLevel>("VIEWER");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const initRef = useRef(false);

  const init = useCallback(async () => {
    if (initRef.current) return;
    initRef.current = true;

    try {
      const token = await getToken();
      const [fetchedUsers, fetchedTeams] = await Promise.all([
        getUsers(plan.organisationId, token || undefined),
        getTeams(plan.organisationId, token || undefined),
      ]);
      setAllUsers(fetchedUsers);
      setAllTeams(fetchedTeams);
    } catch (e) {
      console.error("Failed to initialize ShareModal:", e);
    } finally {
      setInitialLoading(false);
      initRef.current = false;
    }
  }, [plan.organisationId, getToken]);

  useEffect(() => {
    init();
  }, [init]);

  const handleAddShare = async (
    granteeType: "USER" | "TEAM" | "ORGANISATION",
    granteeId: string,
  ) => {
    setLoading(true);
    try {
      const token = await getToken();
      await saveShare(
        plan.id,
        granteeType,
        granteeId,
        selectedPermission,
        token || undefined,
      );
      onUpdate();
      setSearchTerm("");
    } catch (e) {
      alert("Failed to share: " + e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShare = async (
    granteeType: string,
    granteeId: string,
    permission: PermissionLevel,
  ) => {
    setLoading(true);
    try {
      const token = await getToken();
      await saveShare(
        plan.id,
        granteeType,
        granteeId,
        permission,
        token || undefined,
      );
      onUpdate();
    } catch (e) {
      alert("Failed to update share: " + e);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveShare = async (granteeType: string, granteeId: string) => {
    if (!confirm("Are you sure you want to remove access?")) return;
    setLoading(true);
    try {
      const token = await getToken();
      await deleteShare(plan.id, granteeType, granteeId, token || undefined);
      onUpdate();
    } catch (e) {
      alert("Failed to remove share: " + e);
    } finally {
      setLoading(false);
    }
  };

  const existingShareIds = new Set((plan.shares || []).map((s) => s.granteeId));
  const orgShare = (plan.shares || []).find(
    (s) => s.granteeType === "ORGANISATION",
  );

  const filteredUsers = allUsers.filter(
    (u) =>
      u.id !== plan.ownerId &&
      !existingShareIds.has(u.id) &&
      (u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const filteredTeams = allTeams.filter(
    (t) =>
      !existingShareIds.has(t.id) &&
      t.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedShares = [...(plan.shares || [])]
    .filter((s) => s.granteeType !== "ORGANISATION")
    .sort((a, b) => {
      const priority = { TEAM: 0, USER: 1 };
      return priority[a.granteeType] - priority[b.granteeType];
    });

  const getName = (type: string, id: string) => {
    if (type === "ORGANISATION") return `Everyone in ${currentOrg.name}`;
    if (type === "TEAM")
      return allTeams.find((t) => t.id === id)?.name || "Unknown Team";
    if (type === "USER")
      return allUsers.find((u) => u.id === id)?.name || "Unknown User";
    return "Unknown";
  };

  const getEmail = (type: string, id: string) => {
    if (type === "USER") return allUsers.find((u) => u.id === id)?.email;
    return null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-modal flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900 rounded-t-xl">
          <h3 className="font-bold text-lg text-slate-800 dark:text-white flex items-center gap-2">
            Share "{plan.name}"
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition"
          >
            <XCircle size={20} />
          </button>
        </div>

        {initialLoading ? (
          <div className="p-12 flex flex-col items-center justify-center text-slate-400">
            <Loader2 size={32} className="animate-spin mb-3 text-indigo-500" />
            <p className="text-sm">Loading users and teams...</p>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search
                    className="absolute left-3 top-2.5 text-slate-400"
                    size={16}
                  />
                  <input
                    type="text"
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm outline-none transition"
                    placeholder="Add people or teams..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select
                  value={selectedPermission}
                  onChange={(e) =>
                    setSelectedPermission(e.target.value as PermissionLevel)
                  }
                  className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="VIEWER">Viewer</option>
                  <option value="EDITOR">Editor</option>
                </select>
              </div>

              {searchTerm && (
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 shadow-sm">
                  {filteredTeams.map((team) => (
                    <button
                      key={team.id}
                      disabled={loading}
                      onClick={() => handleAddShare("TEAM", team.id)}
                      className="w-full px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between group transition text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                          <Users size={16} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-800 dark:text-white">
                            {team.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            Team
                          </div>
                        </div>
                      </div>
                      <div className="text-xs font-bold text-indigo-600 dark:text-indigo-400 opacity-0 group-hover:opacity-100 transition">
                        Add
                      </div>
                    </button>
                  ))}
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      disabled={loading}
                      onClick={() => handleAddShare("USER", user.id)}
                      className="w-full px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between group transition text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs text-white font-bold bg-indigo-500 overflow-hidden">
                          {user.imageUrl ? (
                            <img
                              src={user.imageUrl}
                              alt={user.initials}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            user.initials
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-800 dark:text-white">
                            {user.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs font-bold text-indigo-600 dark:text-indigo-400 opacity-0 group-hover:opacity-100 transition">
                        Add
                      </div>
                    </button>
                  ))}
                  {filteredTeams.length === 0 && filteredUsers.length === 0 && (
                    <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">
                      No results found
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              <div className="px-4 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">
                People with access
              </div>

              {/* Owner */}
              <div className="px-4 py-3 flex items-center hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition">
                <div className="flex-1 flex items-center gap-3">
                  {(() => {
                    const owner = allUsers.find((u) => u.id === plan.ownerId);
                    return owner ? (
                      <>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs text-white font-bold bg-indigo-500 overflow-hidden">
                          {owner.imageUrl ? (
                            <img
                              src={owner.imageUrl}
                              alt={owner.initials}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            owner.initials
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-800 dark:text-white">
                            {owner.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {owner.email}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700"></div>
                        <div className="text-sm text-slate-500">Owner</div>
                      </>
                    );
                  })()}
                </div>
                <div className="w-32 flex justify-start">
                  <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-1 rounded font-medium">
                    Owner
                  </span>
                </div>
              </div>

              {/* Workspace-wide Access (Everyone) */}
              <div className="px-4 py-3 flex items-center hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition group">
                <div className="flex-1 flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <Globe size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-800 dark:text-white">
                      Everyone in {currentOrg.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Workspace-wide access
                    </div>
                  </div>
                </div>

                <div className="w-32 flex justify-start">
                  <select
                    value={orgShare?.permission || "NONE"}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "NONE")
                        handleRemoveShare("ORGANISATION", plan.organisationId);
                      else
                        handleUpdateShare(
                          "ORGANISATION",
                          plan.organisationId,
                          val as PermissionLevel,
                        );
                    }}
                    disabled={loading}
                    className="bg-transparent text-xs font-medium text-slate-600 dark:text-slate-300 border-none outline-none cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition"
                  >
                    <option value="NONE">None</option>
                    <option value="VIEWER">Viewer</option>
                    <option value="EDITOR">Editor</option>
                  </select>
                </div>
              </div>

              {/* Shares */}
              {sortedShares.map((share) => (
                <div
                  key={share.id}
                  className="px-4 py-3 flex items-center hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition group"
                >
                  <div className="flex-1 flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded flex items-center justify-center overflow-hidden ${
                        share.granteeType === "TEAM"
                          ? "bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                          : "rounded-full bg-indigo-500"
                      }`}
                    >
                      {share.granteeType === "TEAM" && <Users size={16} />}
                      {share.granteeType === "USER" &&
                        (() => {
                          const u = allUsers.find(
                            (u) => u.id === share.granteeId,
                          );
                          return u?.imageUrl ? (
                            <img
                              src={u.imageUrl}
                              alt={u.initials}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-white font-bold">
                              {u?.initials || <User size={14} />}
                            </span>
                          );
                        })()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-800 dark:text-white">
                        {getName(share.granteeType, share.granteeId)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {getEmail(share.granteeType, share.granteeId) ||
                          "Team access"}
                      </div>
                    </div>
                  </div>

                  <div className="w-32 flex items-center justify-start gap-2">
                    <select
                      value={share.permission}
                      onChange={(e) =>
                        handleUpdateShare(
                          share.granteeType,
                          share.granteeId,
                          e.target.value as PermissionLevel,
                        )
                      }
                      disabled={loading}
                      className="bg-transparent text-xs font-medium text-slate-600 dark:text-slate-300 border-none outline-none cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition"
                    >
                      <option value="VIEWER">Viewer</option>
                      <option value="EDITOR">Editor</option>
                    </select>
                    <button
                      onClick={() =>
                        handleRemoveShare(share.granteeType, share.granteeId)
                      }
                      disabled={loading}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg text-sm font-medium transition shadow-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
